import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MIN_ORDER = 30.0;

const Checkout = () => {
  const navigate = useNavigate();
  const { cartItems, getCartTotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    delivery_address: '',
    payment_method: 'pix',
    notes: '',
  });

  useEffect(() => {
    axios.get(`${API}/delivery-zones`).then((r) => setZones(r.data)).catch(() => {});
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const subtotal = getCartTotal();
  const deliveryFee = selectedZone?.fee || 0;
  const total = subtotal + deliveryFee;
  const isRetirar = selectedZone?.name?.toLowerCase().includes('retirar');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (cartItems.length === 0) {
      toast.error('Seu carrinho está vazio!');
      return;
    }

    if (subtotal < MIN_ORDER) {
      toast.error(`Pedido mínimo é de R$ ${MIN_ORDER.toFixed(2)}.`);
      return;
    }

    if (!selectedZone) {
      toast.error('Selecione a zona de entrega');
      return;
    }

    setLoading(true);

    try {
      const orderData = {
        ...formData,
        items: cartItems.map((item) => ({
          product_id: item.id,
          product_name: item.name,
          quantity: item.quantity,
          price: item.price,
        })),
        delivery_zone: selectedZone.name,
        delivery_fee: deliveryFee,
        subtotal,
        total,
      };

      const response = await axios.post(`${API}/orders`, orderData);
      clearCart();
      toast.success('Pedido realizado com sucesso!');
      navigate(`/confirmation/${response.data.id}`);
    } catch (error) {
      toast.error('Erro ao criar pedido. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-[#291014] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Seu carrinho está vazio
          </h2>
          <Button
            onClick={() => navigate('/')}
            className="bg-[#7F1D1D] hover:bg-[#991B1B] text-white rounded-full px-8"
            data-testid="back-to-menu-button"
          >
            Voltar ao Cardápio
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5] py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <Button
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-6 text-[#7F1D1D] hover:text-[#991B1B]"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>

        <h1
          className="text-4xl sm:text-5xl font-bold text-[#291014] mb-8"
          style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.02em' }}
          data-testid="checkout-title"
        >
          Finalizar Pedido
        </h1>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 border border-[#F3E8E3]">
              <h2 className="text-2xl font-bold text-[#291014] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Informações de Entrega
              </h2>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="customer_name">Nome Completo *</Label>
                  <Input
                    id="customer_name"
                    name="customer_name"
                    value={formData.customer_name}
                    onChange={handleChange}
                    required
                    className="mt-1"
                    data-testid="customer-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="customer_phone">Telefone/WhatsApp *</Label>
                  <Input
                    id="customer_phone"
                    name="customer_phone"
                    type="tel"
                    value={formData.customer_phone}
                    onChange={handleChange}
                    required
                    placeholder="(73) 00000-0000"
                    className="mt-1"
                    data-testid="customer-phone-input"
                  />
                </div>

                <div>
                  <Label htmlFor="customer_email">E-mail (opcional)</Label>
                  <Input
                    id="customer_email"
                    name="customer_email"
                    type="email"
                    value={formData.customer_email}
                    onChange={handleChange}
                    className="mt-1"
                    data-testid="customer-email-input"
                  />
                </div>

                <div>
                  <Label>Zona de Entrega *</Label>
                  <Select
                    value={selectedZone?.id || ''}
                    onValueChange={(id) => setSelectedZone(zones.find((z) => z.id === id))}
                  >
                    <SelectTrigger className="mt-1" data-testid="delivery-zone-select">
                      <SelectValue placeholder="Selecione seu bairro ou 'Retirar'" />
                    </SelectTrigger>
                    <SelectContent>
                      {zones.map((z) => (
                        <SelectItem key={z.id} value={z.id} data-testid={`zone-option-${z.id}`}>
                          {z.name} - {z.fee === 0 ? 'Grátis' : `R$ ${z.fee.toFixed(2)}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="delivery_address">
                    {isRetirar ? 'Nome/referência para retirada *' : 'Endereço de Entrega *'}
                  </Label>
                  <Textarea
                    id="delivery_address"
                    name="delivery_address"
                    value={formData.delivery_address}
                    onChange={handleChange}
                    required
                    placeholder={isRetirar ? 'Nome de quem vai retirar' : 'Rua, número, bairro, referência'}
                    className="mt-1"
                    rows={3}
                    data-testid="delivery-address-input"
                  />
                </div>

                <div>
                  <Label className="mb-3 block">Forma de Pagamento *</Label>
                  <RadioGroup
                    value={formData.payment_method}
                    onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
                    data-testid="payment-method-group"
                  >
                    <div className="flex items-center space-x-2 bg-[#FAF8F5] p-4 rounded-lg border border-[#F3E8E3]">
                      <RadioGroupItem value="pix" id="pix" data-testid="payment-pix" />
                      <Label htmlFor="pix" className="flex-1 cursor-pointer">
                        <span className="font-semibold">PIX</span>
                        <p className="text-sm text-[#5C4247]">Pagamento via PIX após confirmação</p>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2 bg-[#FAF8F5] p-4 rounded-lg border border-[#F3E8E3]">
                      <RadioGroupItem value="dinheiro" id="dinheiro" data-testid="payment-cash" />
                      <Label htmlFor="dinheiro" className="flex-1 cursor-pointer">
                        <span className="font-semibold">Dinheiro</span>
                        <p className="text-sm text-[#5C4247]">Pagamento na entrega</p>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2 bg-[#FAF8F5] p-4 rounded-lg border border-[#F3E8E3]">
                      <RadioGroupItem value="cartao" id="cartao" data-testid="payment-card" />
                      <Label htmlFor="cartao" className="flex-1 cursor-pointer">
                        <span className="font-semibold">Cartão na Entrega</span>
                        <p className="text-sm text-[#5C4247]">Máquina de cartão na entrega</p>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                <div>
                  <Label htmlFor="notes">Observações (opcional)</Label>
                  <Textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Alguma observação sobre seu pedido?"
                    className="mt-1"
                    rows={3}
                    data-testid="notes-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full mt-6 bg-[#EA580C] hover:bg-[#C2410C] text-white font-semibold py-6 rounded-full text-lg"
                data-testid="submit-order-button"
              >
                {loading ? 'Processando...' : 'Confirmar Pedido'}
              </Button>
            </form>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 border border-[#F3E8E3] sticky top-6">
              <h2 className="text-2xl font-bold text-[#291014] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="order-summary-title">
                Resumo
              </h2>
              <div className="space-y-3 mb-4" data-testid="order-summary-items">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex justify-between text-sm" data-testid={`summary-item-${item.id}`}>
                    <span style={{ fontFamily: 'Manrope, sans-serif' }}>{item.quantity}x {item.name}</span>
                    <span className="font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      R$ {(item.price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="border-t border-[#F3E8E3] pt-4 space-y-2">
                <div className="flex justify-between text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <span>Subtotal:</span>
                  <span data-testid="order-subtotal">R$ {subtotal.toFixed(2)}</span>
                </div>
                {selectedZone && (
                  <div className="flex justify-between text-sm" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="order-delivery-fee">
                    <span>Entrega ({selectedZone.name}):</span>
                    <span>{deliveryFee === 0 ? 'Grátis' : `R$ ${deliveryFee.toFixed(2)}`}</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-2 border-t border-[#F3E8E3]">
                  <span className="text-lg font-bold text-[#291014]" style={{ fontFamily: 'Outfit, sans-serif' }}>Total:</span>
                  <span className="text-2xl font-bold text-[#7F1D1D]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="order-summary-total">
                    R$ {total.toFixed(2)}
                  </span>
                </div>
                {subtotal < MIN_ORDER && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800 mt-3" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="min-order-warning">
                    Pedido mínimo: R$ {MIN_ORDER.toFixed(2)}. Faltam R$ {(MIN_ORDER - subtotal).toFixed(2)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
